<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">审批中心</h1>
            <p class="list-page-description">处理各类审批申请，包括方案审批、加盟商审核、产品发布等</p>
          </div>
          <div class="list-page-heading-actions">
            <button
              v-permission="'workflow:start'"
              @click="openInitiateModal"
              class="btn-primary"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              发起审批
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- 标签页 -->
    <div class="list-page-table-panel mb-6">
      <div class="border-b border-slate-200">
        <div class="flex space-x-8 px-6 overflow-x-auto">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            @click="activeTab = tab.value"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors relative',
              activeTab === tab.value
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            ]"
          >
            {{ tab.label }}
            <span v-if="tab.count" class="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">{{ tab.count }}</span>
          </button>
        </div>
      </div>

      <!-- 筛选栏 -->
      <PageFilterBar
        shell-class="mb-0 rounded-none border-b border-slate-200 bg-transparent"
        body-class="p-4"
        content-class="flex flex-col gap-3"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="handleRefresh"
      >
        <select v-model="selectedType" class="form-select-native form-control-sm min-w-[148px]">
          <option value="">全部类型</option>
          <option value="proposal">方案审批</option>
          <option value="franchisee">加盟商审核</option>
          <option value="product">产品发布</option>
          <option value="refund">退款审批</option>
          <option value="wealth">财富资金审批</option>
        </select>
        <select v-model="selectedPriority" class="form-select-native form-control-sm min-w-[148px]">
          <option value="">全部优先级</option>
          <option value="urgent">紧急</option>
          <option value="high">高</option>
          <option value="normal">普通</option>
        </select>

        <template #searchActions>
          <button
            type="button"
            class="btn-primary"
            :disabled="loading"
            @click="handleSearch"
          >
            搜索
          </button>
          <button
            type="button"
            class="btn-secondary"
            :disabled="loading"
            @click="handleReset"
          >
            重置
          </button>
        </template>
      </PageFilterBar>

      <!-- 审批列表 -->
      <div class="p-4 space-y-4">
        <!-- 骨架屏加载状态 -->
        <template v-if="loading">
          <div v-for="n in 3" :key="n" class="border border-slate-200 rounded-xl p-4 animate-pulse">
            <div class="flex items-start justify-between mb-3">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-slate-200 rounded-lg"></div>
                <div>
                  <div class="flex items-center gap-2">
                    <div class="h-4 w-32 bg-slate-200 rounded"></div>
                    <div class="h-5 w-14 bg-slate-200 rounded-full"></div>
                  </div>
                  <div class="h-3 w-24 bg-slate-200 rounded mt-2"></div>
                </div>
              </div>
              <div class="text-right">
                <div class="h-3 w-16 bg-slate-200 rounded"></div>
                <div class="h-4 w-20 bg-slate-200 rounded mt-1"></div>
              </div>
            </div>
            <div class="h-16 bg-slate-100 rounded-lg mb-3"></div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-4">
                <div class="h-4 w-24 bg-slate-200 rounded"></div>
                <div class="h-4 w-20 bg-slate-200 rounded"></div>
              </div>
              <div class="flex gap-2">
                <div class="h-8 w-16 bg-slate-200 rounded"></div>
                <div class="h-8 w-16 bg-slate-200 rounded"></div>
              </div>
            </div>
          </div>
        </template>
        <div v-else-if="filteredApprovals.length > 0"
            v-for="approval in filteredApprovals"
            :key="approval.id"
            class="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer"
            @click="openDetailModal(approval)"
          >
          <div class="flex items-start justify-between mb-3">
            <div class="flex items-center gap-3">
              <div :class="['w-10 h-10 rounded-lg flex items-center justify-center', getTypeBg(approval.type)]">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <span class="font-medium text-slate-900">{{ approval.title }}</span>
                  <span :class="['px-2 py-0.5 rounded text-xs font-medium', approval.priorityClass || getPriorityClass(approval.priority)]">
                    {{ approval.priorityText }}
                  </span>
                </div>
                <div class="text-sm text-slate-500">{{ approval.applicant }} | {{ approval.department }}</div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-xs text-slate-500">申请时间</div>
              <div class="text-sm text-slate-700">{{ $formatDateTime(approval.applyTime) }}</div>
            </div>
          </div>

          <div class="bg-slate-50 rounded-lg p-3 mb-3">
            <div class="text-sm text-slate-600">{{ approval.description }}</div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4 text-sm text-slate-500">
              <span v-if="activeTab === 'pending'" class="flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                等待 {{ approval.waitTime }}
              </span>
              <span v-if="approval.amount" class="flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                ¥{{ approval.amount.toLocaleString() }}
              </span>
              <span
                v-if="getApprovalStatusText(approval)"
                :class="[
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                  getApprovalStatusClass(approval)
                ]"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                {{ getApprovalStatusText(approval) }}
              </span>
              <span v-if="approval.currentNodeName && activeTab === 'initiated'" class="flex items-center gap-1">
                {{ approval.currentNodeName }}
              </span>
              <span v-if="approval.currentNodeName && activeTab === 'cc'" class="flex items-center gap-1">
                抄送节点：{{ approval.currentNodeName }}
              </span>
            </div>
            <div v-if="activeTab === 'pending'" class="flex gap-2" @click.stop>
              <button v-permission="'workflow:approve'" @click="openRejectModal(approval)" class="btn-danger-secondary">
                驳回
              </button>
              <button v-permission="'workflow:approve'" @click="approveItem(approval)" class="btn-primary">
                通过
              </button>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="filteredApprovals.length === 0" class="text-center py-12">
          <svg class="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-slate-500">
            {{ emptyStateText }}
          </p>
        </div>
      </div>

      <!-- 分页 -->
      <AppPagination
        v-if="pagination.total > 0"
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="[10, 20, 50]"
        mode="simple"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <!-- 审批详情弹窗 -->
    <ApprovalDetailModal
      v-model="showDetailModal"
      :instance-id="selectedApproval?.instanceId || selectedApproval?.id"
      :type="selectedApproval?.type"
      :priority="selectedApproval?.priority"
      :show-actions="showApprovalActions"
      :show-comment-editor="showApprovalActions"
      title="审批详情"
      @approve="handleApprove"
      @reject="handleReject"
    />

    <!-- 驳回确认弹窗 -->
    <div v-if="showRejectModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="showRejectModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">驳回申请</h3>
            <button @click="showRejectModal = false" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-6">
            <div class="mb-4">
              <div class="text-sm text-slate-500 mb-1">申请标题</div>
              <div class="font-medium text-slate-900">{{ selectedApproval?.title }}</div>
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-slate-700 mb-2">驳回原因 <span class="text-red-500">*</span></label>
              <textarea v-model="rejectForm.reason" rows="4" class="form-textarea" placeholder="请输入驳回原因"></textarea>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="showRejectModal = false" class="btn-secondary">取消</button>
            <button v-permission="'workflow:approve'" @click="confirmReject" class="btn-danger-secondary">确认驳回</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 发起审批弹窗 -->
    <Teleport to="body">
      <div
        v-if="showInitiateModal"
        class="fixed inset-0 z-50 overflow-y-auto"
        @keydown.esc="closeInitiateModal"
      >
        <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" @click="closeInitiateModal"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="modal-panel relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

            <!-- Header -->
            <div class="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">发起审批</h3>
                <p class="text-sm text-slate-500 mt-0.5">
                  {{ currentStep === 1 ? '选择合适的流程模板' : (selectedWorkflow?.templateName || '填写审批信息') }}
                </p>
              </div>
              <button @click="closeInitiateModal" class="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Step Indicator -->
            <div class="modal-stepper">
              <template v-for="(step, index) in wizardSteps" :key="step.value">
                <div class="step-item">
                  <div :class="[
                    'step-circle',
                    currentStep > step.value ? 'is-done' : currentStep === step.value ? 'is-active' : 'is-pending'
                  ]">
                    <svg v-if="currentStep > step.value" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span v-else>{{ index + 1 }}</span>
                  </div>
                  <span :class="['step-label', currentStep >= step.value ? 'is-active' : '']">{{ step.label }}</span>
                </div>
                <div v-if="index < wizardSteps.length - 1" :class="['step-line', currentStep > step.value ? 'is-done' : '']"></div>
              </template>
            </div>

            <!-- Body -->
            <div class="flex-1 overflow-y-auto min-h-0">
              <Transition name="step-fade" mode="out-in">
                <!-- Step 1: 选择流程 -->
                <div v-if="currentStep === 1" key="step-1" class="p-6">
                  <!-- 搜索框 -->
                  <div class="relative mb-4">
                    <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                    <input
                      v-model="workflowSearch"
                      type="text"
                      placeholder="搜索流程名称..."
                      class="form-search-input form-control-sm pr-8"
                    >
                    <button v-if="workflowSearch" @click="workflowSearch = ''" class="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg class="w-4 h-4 text-slate-400 hover:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>

                  <!-- 分类和流程列表 -->
                  <div class="grid grid-cols-12 gap-4 max-h-[58vh] overflow-hidden">
                    <!-- 左侧分类列表 -->
                    <div class="col-span-3 space-y-0.5 overflow-y-auto max-h-[58vh]">
                      <div
                        :class="['category-item', !selectedCategoryId ? 'is-active' : '']"
                        @click="selectedCategoryId = null"
                      >
                        <span class="category-item-icon bg-slate-100">
                          <svg class="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                          </svg>
                        </span>
                        <span>全部</span>
                        <span class="category-item-count">({{ filteredWorkflows.length }})</span>
                      </div>
                      <div class="border-t border-slate-100 my-1 mx-1"></div>
                      <div
                        v-for="category in workflowCategories"
                        :key="category.id"
                        :class="['category-item', selectedCategoryId === category.id ? 'is-active' : '']"
                        @click="selectedCategoryId = category.id"
                      >
                        <span class="category-item-icon bg-slate-100">
                          <svg class="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                          </svg>
                        </span>
                        <span class="truncate">{{ category.categoryName }}</span>
                        <span class="category-item-count">({{ getCategoryWorkflowCount(category.id) }})</span>
                      </div>
                    </div>

                    <!-- 右侧流程卡片 -->
                    <div class="col-span-9 space-y-2 overflow-y-auto max-h-[58vh] pr-1">
                      <!-- 空状态 -->
                      <div v-if="filteredWorkflows.length === 0" class="flex flex-col items-center justify-center py-16">
                        <div class="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                          <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                          </svg>
                        </div>
                        <p class="text-sm text-slate-400">暂无流程</p>
                        <p class="text-xs text-slate-400 mt-1">请尝试其他分类或搜索关键词</p>
                      </div>

                      <!-- 流程卡片 -->
                      <div
                        v-for="workflow in filteredWorkflows"
                        :key="workflow.id"
                        :class="['workflow-card', selectedWorkflow?.id === workflow.id ? 'is-selected' : '']"
                        @click="selectWorkflow(workflow)"
                      >
                        <div :class="['workflow-card-icon', getTypeBg(workflow.categoryCode)]">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                          </svg>
                        </div>
                        <div class="workflow-card-body">
                          <div class="flex items-center gap-2">
                            <span class="workflow-card-title">{{ workflow.templateName }}</span>
                            <span class="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs rounded font-medium">v{{ workflow.version }}</span>
                          </div>
                          <p class="workflow-card-desc">{{ workflow.description || '暂无描述' }}</p>
                          <div class="workflow-card-meta">
                            <span>{{ workflow.nodeCount || 0 }} 个节点</span>
                            <span class="w-1 h-1 rounded-full bg-slate-300"></span>
                            <span>{{ workflow.categoryName }}</span>
                          </div>
                        </div>
                        <div class="flex items-center gap-2 flex-shrink-0">
                          <span :class="['w-2 h-2 rounded-full', workflow.status === 1 ? 'bg-green-500' : 'bg-slate-300']"></span>
                          <svg class="workflow-card-arrow w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Step 2: 填写信息 -->
                <div v-else key="step-2" class="p-6">
                  <!-- 返回按钮 -->
                  <button @click="goBack" class="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                    </svg>
                    返回选择
                  </button>

                  <!-- 流程标题信息条 -->
                  <div class="workflow-info-bar">
                    <div :class="['w-10 h-10 rounded-xl flex items-center justify-center', getTypeBg(selectedWorkflow?.categoryCode)]">
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </div>
                    <div>
                      <span class="text-sm font-semibold text-slate-900">{{ selectedWorkflow?.templateName }}</span>
                      <span class="ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs rounded font-medium">v{{ selectedWorkflow?.version }}</span>
                      <p class="text-xs text-slate-500 mt-0.5">{{ selectedWorkflow?.description || '暂无描述' }}</p>
                    </div>
                  </div>

                  <!-- 表单 -->
                  <el-form :model="initiateForm" label-position="top">
                    <!-- 基本信息区段 -->
                    <div class="form-section">
                      <div class="form-section-header">
                        <svg class="form-section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                        <span class="form-section-title">基本信息</span>
                      </div>
                      <div class="grid grid-cols-3 gap-3">
                        <el-form-item label="申请标题" required class="col-span-2">
                          <el-input v-model="initiateForm.title" placeholder="请输入申请标题" maxlength="100" show-word-limit class="form-control-sm" />
                        </el-form-item>
                        <el-form-item label="优先级">
                          <el-select v-model="initiateForm.priority" class="w-full form-select form-control-sm">
                            <el-option label="普通" value="normal" />
                            <el-option label="高" value="high" />
                            <el-option label="紧急" value="urgent" />
                          </el-select>
                        </el-form-item>
                      </div>
                    </div>

                    <!-- 申请内容区段（动态字段） -->
                    <div v-if="workflowFormFields.length > 0" class="form-section">
                      <div class="form-section-header">
                        <svg class="form-section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                        <span class="form-section-title">申请内容</span>
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <el-form-item
                          v-for="field in workflowFormFields"
                          :key="field.key"
                          :class="field.type === 'TEXTAREA' ? 'col-span-2' : ''"
                        >
                          <template #label>
                            <span class="flex items-center gap-1 text-slate-700">
                              <span>{{ field.label }}</span>
                              <span v-if="field.required" class="text-red-500 font-bold">*</span>
                            </span>
                          </template>
                          <!-- 文本输入框 -->
                          <el-input
                            v-if="['TEXT', 'EMAIL', 'PHONE'].includes(field.type)"
                            v-model="initiateForm.formData[field.key]"
                            :placeholder="`请输入${field.label}`"
                            class="w-full form-control-sm"
                          />
                          <!-- 数字/金额输入 -->
                          <el-input-number
                            v-else-if="['NUMBER', 'MONEY'].includes(field.type)"
                            v-model="initiateForm.formData[field.key]"
                            :min="getWorkflowNumberMin(field)"
                            :max="getWorkflowNumberMax(field)"
                            :precision="field.type === 'MONEY' ? 2 : 0"
                            class="w-full form-number form-control-sm"
                            :placeholder="`请输入`"
                          />
                          <!-- 文本域 -->
                          <el-input
                            v-else-if="field.type === 'TEXTAREA'"
                            v-model="initiateForm.formData[field.key]"
                            type="textarea"
                            :rows="2"
                            :placeholder="`请输入${field.label}`"
                            maxlength="500"
                            class="w-full form-control-sm"
                          />
                          <!-- 日期选择 -->
                          <el-date-picker
                            v-else-if="field.type === 'DATE'"
                            v-model="initiateForm.formData[field.key]"
                            type="date"
                            :placeholder="`请选择${field.label}`"
                            class="w-full form-date form-control-sm"
                            format="YYYY年MM月DD日"
                            value-format="YYYY-MM-DD"
                          />
                          <!-- 日期时间选择 -->
                          <el-date-picker
                            v-else-if="field.type === 'DATETIME'"
                            v-model="initiateForm.formData[field.key]"
                            type="datetime"
                            :placeholder="`请选择${field.label}`"
                            class="w-full form-date form-control-sm"
                            format="YYYY年MM月DD日 HH:mm:ss"
                            value-format="YYYY-MM-DD HH:mm:ss"
                          />
                          <!-- 下拉选择 -->
                          <el-select
                            v-else-if="field.type === 'SELECT'"
                            v-model="initiateForm.formData[field.key]"
                            :placeholder="`请选择${field.label}`"
                            class="w-full form-select form-control-sm"
                            clearable
                          >
                            <el-option v-for="opt in field.options" :key="opt.value" :label="opt.label" :value="opt.value" />
                          </el-select>
                          <!-- 单选按钮 -->
                          <el-radio-group
                            v-else-if="field.type === 'RADIO'"
                            v-model="initiateForm.formData[field.key]"
                            class="flex flex-wrap gap-3"
                          >
                            <el-radio v-for="opt in field.options" :key="opt.value" :label="opt.value">{{ opt.label }}</el-radio>
                          </el-radio-group>
                          <!-- 多选框 -->
                          <el-checkbox-group
                            v-else-if="field.type === 'CHECKBOX'"
                            v-model="initiateForm.formData[field.key]"
                            class="flex flex-wrap gap-3"
                          >
                            <el-checkbox v-for="opt in field.options" :key="opt.value" :label="opt.value">{{ opt.label }}</el-checkbox>
                          </el-checkbox-group>
                          <!-- 开关 -->
                          <el-switch
                            v-else-if="field.type === 'SWITCH'"
                            v-model="initiateForm.formData[field.key]"
                          />
                          <!-- 附件上传 -->
                          <FileUpload
                            v-else-if="field.type === 'FILE'"
                            :files="initiateForm.formData[field.key] || []"
                            :accept="APPROVAL_ATTACHMENT_ACCEPT"
                            :max-count="field.maxCount || 5"
                            :max-size="field.maxSize || 10"
                            @update:files="initiateForm.formData[field.key] = $event"
                            @uploading-change="onWorkflowUploadingChange"
                          />
                        </el-form-item>
                      </div>
                    </div>

                    <!-- 申请说明区段 -->
                    <div class="form-section">
                      <div class="form-section-header">
                        <svg class="form-section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/>
                        </svg>
                        <span class="form-section-title">申请说明</span>
                      </div>
                      <el-input
                        v-model="initiateForm.description"
                        type="textarea"
                        :rows="3"
                        placeholder="请详细描述申请内容..."
                        maxlength="500"
                        show-word-limit
                      />
                    </div>

                    <!-- 审批流程区段 -->
                    <div class="form-section">
                      <div class="form-section-header">
                        <svg class="form-section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <span class="form-section-title">审批流程</span>
                        <span class="form-section-sub">({{ displayedApprovalPath?.displayedNodes?.length || 0 }} 个节点)</span>
                      </div>

                      <div class="min-h-[100px]">
                        <!-- 加载中骨架屏 -->
                        <div v-if="approvalPathLoading && !approvalPathData" class="space-y-2">
                          <div v-for="n in 3" :key="n" class="flex items-center gap-3 p-3 bg-slate-50 rounded-xl animate-pulse">
                            <div class="w-8 h-8 rounded-full bg-slate-200"></div>
                            <div class="flex-1">
                              <div class="h-3 w-24 bg-slate-200 rounded mb-1.5"></div>
                              <div class="h-2 w-32 bg-slate-200 rounded"></div>
                            </div>
                          </div>
                        </div>

                        <!-- 审批节点卡片 -->
                        <div v-else-if="displayedApprovalPath?.displayedNodes?.length" class="space-y-0">
                          <template v-for="(node, index) in displayedApprovalPath.displayedNodes" :key="node.nodeId">
                            <div class="approval-node-card">
                              <div :class="[
                                'approval-node-number',
                                node.nodeType === 'CC' ? 'is-cc' : 'is-approval'
                              ]">
                                {{ index + 1 }}
                              </div>
                              <div class="approval-node-body">
                                <div>
                                  <span class="approval-node-name">{{ node.nodeName }}</span>
                                  <span :class="[
                                    'approval-node-tag',
                                    node.nodeType === 'CC' ? 'is-cc' : 'is-approval'
                                  ]">
                                    {{ node.nodeType === 'CC' ? '抄送' : '审批' }}
                                  </span>
                                  <span v-if="getApprovalModeBadgeText(node)" class="approval-node-mode-tag">
                                    {{ getApprovalModeBadgeText(node) }}
                                  </span>
                                </div>

                                <!-- 审批人员头像+名称 -->
                                <div v-if="node.approvers?.length" class="approval-node-approvers">
                                  <div
                                    v-for="(approver, idx) in node.approvers.slice(0, 3)"
                                    :key="approver.userId || idx"
                                    class="approval-node-approver-item"
                                  >
                                    <div class="approval-node-avatar bg-primary-100 text-primary-600">
                                      <img v-if="approver.avatar" :src="approver.avatar" alt="" />
                                      <span v-else>{{ approver.userName?.charAt(0) || 'U' }}</span>
                                    </div>
                                    <span class="approval-node-approver-name">{{ approver.userName }}</span>
                                  </div>
                                  <span
                                    v-if="node.approvers.length > 3"
                                    class="approval-node-approver-more"
                                  >
                                    +{{ node.approvers.length - 3 }} 人
                                  </span>
                                </div>
                                <div v-else class="approval-node-empty">
                                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                  </svg>
                                  待指定
                                </div>

                                <!-- 必选节点：人员选择器 -->
                                <div v-if="node.requiresSelection" class="mt-3">
                                  <UserSelector
                                    :model-value="selectedUsersForNode(node)"
                                    :max-select="node.maxSelect || null"
                                    :exclude-ids="approverExcludeIdsForNode(node)"
                                    :hide-add-button="!!node.maxSelect && selectedUsersForNode(node).length >= node.maxSelect"
                                    multiple
                                    :placeholder="(node.selectionType === 'CC' ? '请选择抄送人' : '请选择审批人')"
                                    @update:modelValue="(val) => setSelectedUsersForNode(node, val)"
                                  />

                                  <!-- 选择提示 -->
                                  <div class="mt-1.5 text-[11px] text-slate-400">
                                    <span v-if="node.selectionRequired">必选</span>
                                    <span v-else>可选</span>
                                    <span v-if="node.selectionRequired && node.minSelect">，至少 {{ node.minSelect }} 人</span>
                                    <span v-if="node.maxSelect">，最多 {{ node.maxSelect }} 人</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- 节点连接线 -->
                            <div v-if="index < displayedApprovalPath.displayedNodes.length - 1" class="approval-node-connector">
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                              </svg>
                            </div>
                          </template>
                        </div>

                        <!-- 无数据 -->
                        <div v-else class="text-center py-8 bg-slate-50 rounded-xl text-sm text-slate-400">
                          暂无法加载审批路径
                        </div>
                      </div>
                    </div>
                  </el-form>
                </div>
              </Transition>
            </div>

            <!-- Footer -->
            <div class="flex-shrink-0 px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <button v-if="currentStep === 2" @click="goBack" class="btn-secondary">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                  上一步
                </button>
              </div>
              <div class="flex gap-3">
                <button @click="closeInitiateModal" class="btn-secondary">取消</button>
                <button
                  v-if="currentStep === 1"
                  v-permission="'workflow:start'"
                  @click="selectedWorkflow ? (currentStep = 2) : null"
                  :disabled="!selectedWorkflow"
                  class="btn-primary"
                >
                  下一步：填写信息
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
                <button
                  v-if="currentStep === 2"
                  v-permission="'workflow:start'"
                  @click="submitInitiate"
                  :disabled="!canSubmitInitiate"
                  class="btn-primary"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  提交审批
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Teleport>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import UserSelector from '@/components/UserSelector/index.vue'
import { getCategoriesWithTemplates, previewApprovalPath, getTemplateDetail, startApproval, getMyTasks, getMyInstances, getMyCcRecords, markCcRead, approveTask } from '@/api/flow'
import ApprovalDetailModal from '@/components/ApprovalDetailModal.vue'
import FileUpload from '@/components/FileUpload.vue'
import { useApprovalToast } from '@/composables/useApprovalToast'
import { APPROVAL_ATTACHMENT_ACCEPT } from '@/utils/fileAccept'
import { useUserStore } from '@/stores/user'

const wizardSteps = [
  { value: 1, label: '选择流程' },
  { value: 2, label: '填写信息' }
]

const userStore = useUserStore()

const activeTab = ref('pending')
const selectedType = ref('')
const selectedPriority = ref('')
const loading = ref(false)
const ccUnreadCount = ref(0)
const showDetailModal = ref(false)
const showRejectModal = ref(false)
const selectedApproval = ref(null)
const { syncHandledApproval } = useApprovalToast()
const route = useRoute()

// 分页数据
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0,
  totalPending: 0
})

// 发起审批相关状态
const showInitiateModal = ref(false)
const workflowSearch = ref('')
const selectedCategoryId = ref(null)
const currentStep = ref(1)
const selectedWorkflow = ref(null)
const workflowCategories = ref([])
const workflowTemplates = ref([])
const workflowLoading = ref(false)
const workflowFileUploading = ref(0)

// 审批路径数据
const approvalPathData = ref(null)
const approvalPathLoading = ref(false)
const participantSelections = reactive({
  approvers: {},
  cc: {}
})

// 记录上一次表单数据中条件字段的值，用于判断是否需要刷新审批路径
let lastFormDataValues = {}

// 发起审批表单
const initiateForm = reactive({
  title: '',
  priority: 'normal',
  amount: null,
  description: '',
  formData: {}
})

const approvalForm = reactive({
  comment: ''
})

const rejectForm = reactive({
  reason: ''
})

const tabs = computed(() => [
  { label: '待审批', value: 'pending', count: pagination.totalPending },
  { label: '已审批', value: 'completed', count: 0 },
  { label: '我发起', value: 'initiated', count: 0 },
  { label: '抄送我', value: 'cc', count: ccUnreadCount.value }
])

const approvals = ref([])

const routeReadonly = computed(() => route.query.readonly === 'true')

const showApprovalActions = computed(() => {
  return activeTab.value === 'pending' &&
    selectedApproval.value?.isTask &&
    !routeReadonly.value
})

function applyApprovalTabFromRouteQuery() {
  const routeTab = Array.isArray(route.query.tab) ? route.query.tab[0] : route.query.tab
  const validTabs = ['pending', 'completed', 'initiated', 'cc']
  if (!validTabs.includes(routeTab)) return false
  if (activeTab.value === routeTab) return false
  activeTab.value = routeTab
  return true
}

// 优先级映射
const priorityMap = {
  0: { value: 'normal', text: '普通', class: 'bg-slate-100 text-slate-600' },
  1: { value: 'high', text: '高优先级', class: 'bg-amber-100 text-amber-700' },
  2: { value: 'urgent', text: '紧急', class: 'bg-red-100 text-red-700' }
}

// 优先级字符串转数字
const priorityValueToNum = {
  'normal': 0,
  'high': 1,
  'urgent': 2
}

const approvalStatusStyles = {
  APPROVING: 'bg-blue-100 text-blue-700 ring-1 ring-blue-200',
  APPROVED: 'bg-green-100 text-green-700 ring-1 ring-green-200',
  REJECTED: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  CANCELLED: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
  CANCELED: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
  PENDING: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
  READ: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
  COMPLETED: 'bg-green-100 text-green-700 ring-1 ring-green-200'
}

const approvalStatusTextMap = {
  APPROVING: '审批中',
  APPROVED: '已通过',
  REJECTED: '已驳回',
  CANCELLED: '已取消',
  CANCELED: '已取消',
  PENDING: '待处理',
  READ: '已读',
  COMPLETED: '已处理'
}

function resolveApprovalStatus(approval) {
  if (!approval) return ''
  if (approval.isCcRecord && approval.ccStatus) {
    return String(approval.ccStatus).toUpperCase()
  }
  if (approval.status) {
    return String(approval.status).toUpperCase()
  }
  if (activeTab.value === 'pending') {
    return 'PENDING'
  }
  return ''
}

function getApprovalStatusClass(approval) {
  const status = resolveApprovalStatus(approval)
  return approvalStatusStyles[status] || 'bg-slate-100 text-slate-700 ring-1 ring-slate-200'
}

function getApprovalStatusText(approval) {
  if (!approval) return ''
  const status = resolveApprovalStatus(approval)
  return approvalStatusTextMap[status] || approval.statusDesc || ''
}

function getApprovalModeBadgeText(node) {
  if (node?.nodeType !== 'APPROVAL') return ''
  if (!node?.approvers?.length || node?.approvers?.length <= 1) return ''
  if (node.approvalMode === 'ALL') return '会签'
  if (node.approvalMode === 'ANY') return '或签'
  return ''
}

// 加载待办任务
async function loadMyTasks() {
  loading.value = true
  try {
    const res = await getMyTasks({ pageNum: pagination.current, pageSize: pagination.size, status: 'PENDING' })
    const pageData = res || {}
    approvals.value = (pageData.records || []).map(task => normalizeApproval(task, true))
    pagination.total = pageData.total || 0
    pagination.totalPending = pageData.total || 0
  } catch (error) {
    console.error('加载待办任务失败:', error)
    ElMessage.error(error.message || '加载待办任务失败')
  } finally {
    loading.value = false
  }
}

// 加载已审批任务
async function loadCompletedTasks() {
  loading.value = true
  try {
    const res = await getMyTasks({ pageNum: pagination.current, pageSize: pagination.size, status: 'COMPLETED' })
    const pageData = res || {}
    approvals.value = (pageData.records || []).map(instance => normalizeApproval(instance, false))
    pagination.total = pageData.total || 0
  } catch (error) {
    console.error('加载已审批任务失败:', error)
    ElMessage.error(error.message || '加载失败')
  } finally {
    loading.value = false
  }
}

// 加载我发起的审批
async function loadMyInstances() {
  loading.value = true
  try {
    const res = await getMyInstances({ pageNum: pagination.current, pageSize: pagination.size })
    const pageData = res || {}
    approvals.value = (pageData.records || []).map(instance => normalizeApproval(instance, false))
    pagination.total = pageData.total || 0
  } catch (error) {
    console.error('加载我发起的审批失败:', error)
    ElMessage.error(error.message || '加载失败')
  } finally {
    loading.value = false
  }
}

// 加载抄送给我的审批
async function loadMyCcRecords() {
  loading.value = true
  try {
    const res = await getMyCcRecords({ pageNum: pagination.current, pageSize: pagination.size })
    const records = Array.isArray(res) ? res : (res?.records || [])
    approvals.value = records.map(record => normalizeCcRecord(record))
    pagination.total = Array.isArray(res) ? records.length : (res?.total || records.length)
    await loadCcUnreadCount()
  } catch (error) {
    console.error('加载抄送记录失败:', error)
    ElMessage.error(error.message || '加载抄送记录失败')
  } finally {
    loading.value = false
  }
}

async function loadCcUnreadCount() {
  try {
    const res = await getMyCcRecords({ unreadOnly: true })
    const records = Array.isArray(res) ? res : (res?.records || [])
    ccUnreadCount.value = Array.isArray(res) ? records.length : (res?.total || records.length)
  } catch (error) {
    console.error('加载抄送未读数失败:', error)
  }
}

function normalizeApproval(source, isTask) {
  const formData = source.formData || {}
  const type = resolveApprovalType(source)
  const amount = formData.amount === null || formData.amount === undefined || formData.amount === ''
    ? null
    : Number(formData.amount)

  return {
    id: source.id,
    instanceId: source.instanceId,
    instanceNo: source.instanceNo,
    title: source.title,
    applicant: source.applicantName,
    department: '',
    type,
    typeText: resolveApprovalTypeText(type, source),
    priority: priorityMap[source.priority]?.value || 'normal',
    priorityText: source.priorityDesc || priorityMap[source.priority]?.text || '普通',
    priorityClass: priorityMap[source.priority]?.class || 'bg-slate-100 text-slate-600',
    amount: Number.isFinite(amount) ? amount : null,
    description: source.description,
    applyTime: source.createTime,
    waitTime: formatWaitTime(source.createTime),
    taskNodeName: source.nodeName,
    status: source.status,
    statusDesc: source.statusDesc,
    currentNodeName: source.currentNodeName,
    templateCode: source.templateCode,
    bizType: source.bizType,
    bizId: source.bizId || formData.applyId,
    formData,
    attachments: [],
    history: [],
    isTask
  }
}

function normalizeCcRecord(record) {
  const formData = record.formData || {}
  const type = resolveApprovalType(record)
  const amount = formData.amount === null || formData.amount === undefined || formData.amount === ''
    ? null
    : Number(formData.amount)
  const applyTime = record.applyTime || record.createTime
  return {
    id: `cc-${record.id}`,
    ccRecordId: record.id,
    instanceId: record.instanceId,
    instanceNo: record.instanceNo,
    title: record.title,
    applicant: record.applicantName,
    department: '',
    type,
    typeText: record.templateName,
    priority: priorityMap[record.priority]?.value || 'normal',
    priorityText: record.priorityDesc || priorityMap[record.priority]?.text || '普通',
    priorityClass: priorityMap[record.priority]?.class || 'bg-slate-100 text-slate-600',
    amount: Number.isFinite(amount) ? amount : null,
    description: record.description,
    applyTime: record.applyTime || record.createTime,
    waitTime: formatWaitTime(applyTime),
    taskNodeName: record.nodeName,
    status: record.instanceStatus,
    statusDesc: record.status === 'READ' ? '已读' : '未读',
    currentNodeName: record.currentNodeName,
    templateCode: record.templateCode,
    bizType: record.bizType,
    bizId: record.bizId || formData.applyId,
    formData,
    attachments: [],
    history: [],
    isTask: false,
    isCcRecord: true,
    ccStatus: record.status
  }
}

function resolveApprovalType(source) {
  const formData = source.formData || {}
  if (source.bizType === 'wealth_fund_operation_approval' ||
      source.templateCode === 'WEALTH_FUND_OPERATION_FLOW' ||
      formData?.bizType === 'wealth_fund_operation_approval' ||
      formData?.applyId) {
    return 'wealth'
  }
  if (source.bizType === 'customer_referrer_change_approval') return 'customer'
  if (source.bizType === 'order_amount_change_approval') return 'order'
  if (source.templateCode === 'PLAN_APPROVAL_FLOW') return 'proposal'
  return 'proposal'
}

function resolveApprovalTypeText(type, source) {
  if (type === 'wealth') return '财富资金审批'
  if (type === 'customer') return '客户审批'
  if (type === 'order') return '订单审批'
  return source.templateName || '审批'
}

// 格式化等待时间
function formatWaitTime(createTime) {
  if (!createTime) return '刚刚'
  const now = new Date()
  const create = new Date(createTime)
  const diff = Math.floor((now - create) / 1000 / 60) // 分钟

  if (diff < 1) return '刚刚'
  if (diff < 60) return `${diff}分钟`
  if (diff < 1440) return `${Math.floor(diff / 60)}小时`
  return `${Math.floor(diff / 1440)}天`
}

// 打开详情弹窗
const openDetailModal = async (approval) => {
  if (activeTab.value === 'cc') {
    await markCcRecordRead(approval)
  }
  selectedApproval.value = approval
  approvalForm.comment = ''
  showDetailModal.value = true
}

async function markCcRecordRead(approval) {
  if (!approval?.isCcRecord || approval.ccStatus === 'READ' || !approval.ccRecordId) return
  try {
    await markCcRead(approval.ccRecordId)
    approval.ccStatus = 'READ'
    approval.status = 'READ'
    approval.statusDesc = '已读'
    if (ccUnreadCount.value > 0) {
      ccUnreadCount.value -= 1
    }
  } catch (error) {
    console.error('标记抄送已读失败:', error)
  }
}

async function openDetailFromRouteQuery() {
  if (route.query.openDetail !== 'true') return
  const instanceId = route.query.id
  if (!instanceId) return

  const rawCcRecordId = Array.isArray(route.query.ccRecordId)
    ? route.query.ccRecordId[0]
    : route.query.ccRecordId
  const ccRecordId = rawCcRecordId ? String(rawCcRecordId) : ''
  if (activeTab.value === 'cc' && ccRecordId) {
    try {
      await markCcRead(ccRecordId)
      await loadMyCcRecords()
    } catch (error) {
      console.error('标记路由抄送已读失败:', error)
    }
  }

  selectedApproval.value = {
    id: Number(instanceId),
    instanceId: Number(instanceId),
    type: selectedType.value || 'proposal',
    priority: 'normal',
    isTask: !routeReadonly.value
  }
  approvalForm.comment = ''
  showDetailModal.value = true
}

// 通过审批
const handleApprove = async ({ comment }) => {
  try {
    await approveTask({
      instanceId: selectedApproval.value.instanceId || selectedApproval.value.id,
      actionType: 'AGREE',
      remark: comment
    })
    ElMessage.success('审批通过')
    syncHandledApproval(selectedApproval.value.id)
    showDetailModal.value = false
    loadApprovalsByTab()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// 驳回审批
const handleReject = () => {
  rejectForm.reason = ''
  showRejectModal.value = true
  showDetailModal.value = false
}
async function loadApprovalsByTab() {
  if (activeTab.value === 'pending') {
    await loadMyTasks()
  } else if (activeTab.value === 'initiated') {
    await loadMyInstances()
  } else if (activeTab.value === 'cc') {
    await loadMyCcRecords()
  } else {
    // 已审批
    await loadCompletedTasks()
  }
}

function handleSearch() {
  pagination.current = 1
  loadApprovalsByTab()
}

function handleReset() {
  selectedType.value = ''
  selectedPriority.value = ''
  pagination.current = 1
  loadApprovalsByTab()
}

function handleRefresh() {
  loadCcUnreadCount()
  loadApprovalsByTab()
}

// 弹窗打开时锁定body滚动
watch(showInitiateModal, (val) => {
  document.body.style.overflow = val ? 'hidden' : ''
})

// 切换tab时重新加载
watch(activeTab, () => {
  pagination.current = 1
  loadApprovalsByTab()
})

// 分页切换
function handlePageChange(page) {
  pagination.current = page
  loadApprovalsByTab()
}

function handlePageSizeChange(size) {
  pagination.size = size
  pagination.current = 1
  loadApprovalsByTab()
}

// 初始化加载
onMounted(() => {
  loadCcUnreadCount()
  const tabChanged = applyApprovalTabFromRouteQuery()
  if (!tabChanged) {
    loadApprovalsByTab()
  }
  openDetailFromRouteQuery()
})

watch(
  () => route.query,
  () => {
    applyApprovalTabFromRouteQuery()
    openDetailFromRouteQuery()
  }
)

const filteredApprovals = computed(() => {
  return approvals.value.filter(a => {
    const matchType = !selectedType.value || a.type === selectedType.value
    const matchPriority = !selectedPriority.value || a.priority === selectedPriority.value
    // 待办tab显示所有加载的待办数据
    // 我发起/已审批tab显示所有加载的数据
    return matchType && matchPriority
  })
})

const emptyStateText = computed(() => {
  if (activeTab.value === 'pending') return '暂无待审批事项'
  if (activeTab.value === 'initiated') return '暂无我发起的审批'
  if (activeTab.value === 'cc') return '暂无抄送给我的审批'
  return '暂无已审批记录'
})

const canSubmitInitiate = computed(() => {
  if (!initiateForm.title || workflowFileUploading.value > 0) return false
  if (approvalPathLoading.value || !approvalPathData.value) return false
  if (hasMissingRequiredParticipantSelections()) return false
  return true
})

// 发起审批计算属性
const filteredWorkflows = computed(() => {
  let workflows = workflowTemplates.value.filter(w => w.status === 1) // 只显示已启用的流程

  if (workflowSearch.value) {
    const search = workflowSearch.value.toLowerCase()
    workflows = workflows.filter(w =>
      w.templateName.toLowerCase().includes(search) ||
      w.description?.toLowerCase().includes(search)
    )
  }

  if (selectedCategoryId.value) {
    workflows = workflows.filter(w => w.categoryCode)
  }

  return workflows
})

const workflowFormFields = computed(() => {
  if (!selectedWorkflow.value?.formSchema) return []

  try {
    const formSchema = typeof selectedWorkflow.value.formSchema === 'string'
      ? JSON.parse(selectedWorkflow.value.formSchema)
      : selectedWorkflow.value.formSchema
    return formSchema?.fields || []
  } catch {
    return []
  }
})

function getWorkflowNumberMin(field) {
  return normalizeWorkflowNumberBound(field?.min)
}

function getWorkflowNumberMax(field) {
  return normalizeWorkflowNumberBound(field?.max)
}

function normalizeWorkflowNumberBound(value) {
  if (value === null || value === undefined || value === '') return undefined
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : undefined
}

// 根据条件判断实际展示的流程路径（跳过条件节点，类似钉钉设计）
const displayedApprovalPath = computed(() => {
  if (!approvalPathData.value) return null

  const pathNodes = approvalPathData.value.pathNodes || []
  const conditionBranches = approvalPathData.value.conditionBranches || []
  const formData = initiateForm.formData

  // 构建节点映射
  const nodeMap = new Map()
  pathNodes.forEach(node => {
    nodeMap.set(node.nodeId, node)
  })

  // 条件判断函数
  function evaluateCondition(fieldValue, operator, branchValue) {
    // 如果字段值为空，默认不匹配
    if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
      return false
    }

    // 转换类型进行比较
    const fv = fieldValue
    const bv = branchValue

    switch (operator) {
      case 'EQ':
        return String(fv) === String(bv)
      case 'NEQ':
        return String(fv) !== String(bv)
      case 'GT':
        return Number(fv) > Number(bv)
      case 'GTE':
        return Number(fv) >= Number(bv)
      case 'LT':
        return Number(fv) < Number(bv)
      case 'LTE':
        return Number(fv) <= Number(bv)
      case 'IN':
        if (Array.isArray(bv)) {
          return bv.includes(fv)
        }
        return String(bv).split(',').map(s => s.trim()).includes(String(fv))
      case 'CONTAINS':
        return String(fv).includes(String(bv))
      default:
        return false
    }
  }

  // 构建实际展示的审批节点列表
  const displayedNodes = []

  if (conditionBranches.length > 0) {
    // 有条件分支时，根据表单数据判断走哪个分支
    const condition = conditionBranches[0]
    let targetNodeId = null

    if (condition?.branches) {
      // 遍历条件分支，查找匹配的分支
      for (const branch of condition.branches) {
        const fieldValue = formData[branch.field]
        if (evaluateCondition(fieldValue, branch.operator, branch.value)) {
          targetNodeId = branch.targetNodeId
          break
        }
      }
      // 如果没有匹配的分支，默认走第一个分支
      if (!targetNodeId && condition.branches.length > 0) {
        targetNodeId = condition.branches[0].targetNodeId
      }
    }

    // 按照 nextNodeId 遍历
    let currentNodeId = targetNodeId
    let loopCount = 0
    while (currentNodeId && loopCount < 20) {
      const node = nodeMap.get(currentNodeId)
      if (!node) {
        break
      }

      // 只添加 APPROVAL 和 CC 类型的节点
      if (node.nodeType === 'APPROVAL' || node.nodeType === 'CC') {
        displayedNodes.push(node)
      }

      // 继续按 nextNodeId 遍历
      currentNodeId = node.nextNodeId
      loopCount++
    }
  } else {
    // 没有条件分支时，按照 nextNodeId 展示所有审批节点
    // 找到起始节点（第一个审批节点）
    const startNode = pathNodes.find(n => n.nodeType === 'APPROVAL' || n.nodeType === 'CC')
    let currentNodeId = startNode?.nodeId

    while (currentNodeId) {
      const node = nodeMap.get(currentNodeId)
      if (!node) break

      // 只添加 APPROVAL 和 CC 类型的节点
      if (node.nodeType === 'APPROVAL' || node.nodeType === 'CC') {
        displayedNodes.push(node)
      }

      // 继续按 nextNodeId 遍历
      currentNodeId = node.nextNodeId
    }
  }

  return {
    ...approvalPathData.value,
    displayedNodes
  }
})

// 发起审批方法
async function loadWorkflowData() {
  workflowLoading.value = true
  try {
    const res = await getCategoriesWithTemplates()
    const data = res.data || res || []

    // 转换为扁平化的分类和模板列表
    workflowCategories.value = data.map(item => ({
      id: item.categoryId,
      categoryCode: item.categoryCode,
      categoryName: item.categoryName
    }))

    // 收集所有模板
    const templates = []
    data.forEach(item => {
      if (item.templates && item.templates.length > 0) {
        item.templates.forEach(t => {
          templates.push({
            id: t.id,
            templateCode: t.templateCode,
            templateName: t.templateName,
            description: t.description,
            version: t.version,
            categoryId: item.categoryId,
            categoryCode: item.categoryCode,
            categoryName: item.categoryName,
            status: 1 // 新接口返回的模板都是启用的
          })
        })
      }
    })
    workflowTemplates.value = templates
  } catch (error) {
    console.error('加载流程数据失败:', error)
    ElMessage.error(error.message || '加载流程数据失败')
  } finally {
    workflowLoading.value = false
  }
}

function openInitiateModal() {
  showInitiateModal.value = true
  currentStep.value = 1
  selectedWorkflow.value = null
  selectedCategoryId.value = null
  workflowSearch.value = ''
  resetInitiateForm()
  loadWorkflowData()
}

function closeInitiateModal() {
  showInitiateModal.value = false
  selectedWorkflow.value = null
  approvalPathData.value = null
  clearParticipantSelections()
}

function goBack() {
  currentStep.value = 1
}

function resetInitiateForm() {
  initiateForm.title = ''
  initiateForm.priority = 'normal'
  initiateForm.amount = null
  initiateForm.description = ''
  initiateForm.formData = {}
  initiateForm.attachments = []
  approvalPathData.value = null
  clearParticipantSelections()
}

function clearParticipantSelections() {
  Object.keys(participantSelections.approvers).forEach(key => delete participantSelections.approvers[key])
  Object.keys(participantSelections.cc).forEach(key => delete participantSelections.cc[key])
}

const currentUser = computed(() => userStore.userInfo || {})

// 当节点不允许选发起人时，从可选列表中排除当前用户
function approverExcludeIdsForNode(node) {
  if (node?.allowApplicant === false && currentUser.value?.id) {
    return [currentUser.value.id]
  }
  return []
}

function currentSelectionNodeIds() {
  const nodes = displayedApprovalPath.value?.displayedNodes || approvalPathData.value?.pathNodes || []
  return new Set(nodes.filter(node => node.requiresSelection).map(node => node.nodeId))
}

function syncSelectionsWithDisplayedPath() {
  const validNodeIds = currentSelectionNodeIds()
  Object.keys(participantSelections.approvers).forEach(nodeId => {
    if (!validNodeIds.has(nodeId)) delete participantSelections.approvers[nodeId]
  })
  Object.keys(participantSelections.cc).forEach(nodeId => {
    if (!validNodeIds.has(nodeId)) delete participantSelections.cc[nodeId]
  })
}

function selectedUsersForNode(node) {
  return node.selectionType === 'CC'
    ? (participantSelections.cc[node.nodeId] || [])
    : (participantSelections.approvers[node.nodeId] || [])
}

function setSelectedUsersForNode(node, value) {
  const ids = Array.isArray(value) ? value : []
  if (node.selectionType === 'CC') {
    participantSelections.cc[node.nodeId] = ids
  } else {
    participantSelections.approvers[node.nodeId] = ids
  }
  loadApprovalPath(true)
}

function getWorkflowAttachments(field) {
  const value = initiateForm.formData[field.key]
  return Array.isArray(value) ? value : []
}

function onWorkflowUploadingChange(uploading) {
  if (uploading) {
    workflowFileUploading.value += 1
  } else {
    workflowFileUploading.value = Math.max(0, workflowFileUploading.value - 1)
  }
}

function buildSubmitFormData() {
  const formData = { ...initiateForm.formData }
  workflowFormFields.value
    .filter(field => field.type === 'FILE')
    .forEach(field => {
      formData[field.key] = getWorkflowAttachments(field).map(file => ({
        name: file.name,
        fileName: file.fileName || file.name,
        originalName: file.originalName || file.name,
        fileKey: file.fileKey,
        bucket: file.bucket || 'private',
        size: file.size,
        fileSize: file.fileSize || file.size,
        contentType: file.contentType
      }))
    })
  return formData
}

function validateParticipantSelections() {
  const pathNodes = displayedApprovalPath.value?.displayedNodes || approvalPathData.value?.pathNodes || []
  for (const node of pathNodes) {
    if (!node.requiresSelection) continue
    const selected = selectedUsersForNode(node)
    const min = node.selectionRequired ? (node.minSelect ?? 1) : 0
    if (node.selectionRequired && selected.length < min) {
      ElMessage.warning(`请选择${node.nodeName}的${node.selectionType === 'CC' ? '抄送人' : '审批人'}`)
      return false
    }
    if (node.maxSelect && selected.length > node.maxSelect) {
      ElMessage.warning(`${node.nodeName}最多选择${node.maxSelect}人`)
      return false
    }
  }
  return true
}

function hasMissingRequiredParticipantSelections() {
  const pathNodes = displayedApprovalPath.value?.displayedNodes || approvalPathData.value?.pathNodes || []
  for (const node of pathNodes) {
    if (!node.requiresSelection) continue
    const selected = selectedUsersForNode(node)
    const min = node.selectionRequired ? (node.minSelect ?? 1) : 0
    if (node.selectionRequired && selected.length < min) {
      return true
    }
  }
  return false
}

function getCategoryWorkflowCount(categoryId) {
  return filteredWorkflows.value.filter(w => {
    const category = workflowCategories.value.find(c => c.id === categoryId)
    return category && w.categoryCode === category.categoryCode
  }).length
}

function getWorkflowFormInitialValue(field) {
  if (field.type === 'FILE' || field.type === 'CHECKBOX') {
    return []
  }
  return null
}

function normalizeWorkflowFormValue(field, value) {
  if (field.type === 'FILE' || field.type === 'CHECKBOX') {
    return Array.isArray(value) ? value : []
  }
  return value
}

function initializeWorkflowFormData(fields, { preserveExisting = false } = {}) {
  const previousFormData = { ...initiateForm.formData }
  const nextFormData = {}
  fields.forEach(field => {
    if (preserveExisting && Object.prototype.hasOwnProperty.call(previousFormData, field.key)) {
      nextFormData[field.key] = normalizeWorkflowFormValue(field, previousFormData[field.key])
      return
    }
    nextFormData[field.key] = getWorkflowFormInitialValue(field)
  })
  initiateForm.formData = nextFormData
}

async function selectWorkflow(workflow) {
  try {
    const sameWorkflowSelected = selectedWorkflow.value?.id === workflow.id
    if (!sameWorkflowSelected) {
      clearParticipantSelections()
      approvalPathData.value = null
    }
    // 先根据ID获取完整的流程详情
    const res = await getTemplateDetail(workflow.id)
    const detailData = res.data || res

    // 更新选中的工作流，包含完整的formSchema和其他详情
    selectedWorkflow.value = {
      ...workflow,
      formSchema: detailData.formSchema,
      processNodes: detailData.processNodes,
      description: detailData.description || workflow.description
    }

    currentStep.value = 2

    // 初始化表单数据
    initializeWorkflowFormData(workflowFormFields.value, { preserveExisting: sameWorkflowSelected })

    // 重置审批路径缓存
    if (!sameWorkflowSelected) {
      lastFormDataValues = {}
    }

    // 加载审批路径
    loadApprovalPath()
  } catch (error) {
    console.error('加载流程详情失败:', error)
    ElMessage.error(error.message || '加载流程详情失败')
  }
}

// 加载审批路径
async function loadApprovalPath(forceRefresh = false) {
  if (!selectedWorkflow.value) return false

  // 如果不是强制刷新，检查条件是否真的会影响审批路径
  if (!forceRefresh && approvalPathData.value) {
    const conditionBranches = approvalPathData.value.conditionBranches || []

    // 如果没有条件分支，不需要刷新
    if (conditionBranches.length === 0) {
      return true
    }

    // 获取条件分支的边界值
    const branchThresholds = conditionBranches[0]?.branches
      ?.filter(b => b.operator === 'GTE' || b.operator === 'GT' || b.operator === 'LTE' || b.operator === 'LT' || b.operator === 'EQ')
      .map(b => ({ field: b.field, value: Number(b.value), operator: b.operator }))
      || []

    // 如果有条件分支，检查当前值是否跨越了边界
    let needsRefresh = false
    for (const threshold of branchThresholds) {
      const currentValue = Number(initiateForm.formData[threshold.field] ?? 0)
      const lastValue = Number(getLastFormDataValue(threshold.field) ?? 0)

      // 检查是否跨越了边界
      if (threshold.operator === 'GTE') {
        if ((lastValue < threshold.value && currentValue >= threshold.value) ||
            (lastValue >= threshold.value && currentValue < threshold.value)) {
          needsRefresh = true
          break
        }
      } else if (threshold.operator === 'GT') {
        if ((lastValue <= threshold.value && currentValue > threshold.value) ||
            (lastValue > threshold.value && currentValue <= threshold.value)) {
          needsRefresh = true
          break
        }
      } else if (threshold.operator === 'LTE') {
        if ((lastValue > threshold.value && currentValue <= threshold.value) ||
            (lastValue <= threshold.value && currentValue > threshold.value)) {
          needsRefresh = true
          break
        }
      } else if (threshold.operator === 'LT') {
        if ((lastValue >= threshold.value && currentValue < threshold.value) ||
            (lastValue < threshold.value && currentValue >= threshold.value)) {
          needsRefresh = true
          break
        }
      } else if (threshold.operator === 'EQ') {
        if (lastValue !== currentValue) {
          needsRefresh = true
          break
        }
      }
    }

    // 更新记录的值
    updateLastFormDataValues()

    // 如果不需要刷新，直接返回
    if (!needsRefresh) {
      return true
    }
  }

  // 记录当前值用于下次比较
  updateLastFormDataValues()
  approvalPathLoading.value = true
  try {
    const res = await previewApprovalPath(selectedWorkflow.value.id, {
      formData: initiateForm.formData,
      selectedApproverUserIds: participantSelections.approvers,
      selectedCcUserIds: participantSelections.cc
    })
    approvalPathData.value = res.data || res
    syncSelectionsWithDisplayedPath()
    return true
  } catch (error) {
    console.error('加载审批路径失败:', error)
    approvalPathData.value = null
    ElMessage.error(error.message || '加载审批路径失败')
    return false
  } finally {
    approvalPathLoading.value = false
  }
}

// 更新上一次表单数据中条件字段的值
lastFormDataValues = {}
function updateLastFormDataValues() {
  const conditionFields = workflowFormFields.value
    .filter(f => f.type === 'NUMBER' || f.type === 'MONEY' || f.type === 'SELECT')
  conditionFields.forEach(f => {
    lastFormDataValues[f.key] = initiateForm.formData[f.key]
  })
}
function getLastFormDataValue(key) {
  return lastFormDataValues[key]
}

function isWorkflowFieldEmpty(field, value) {
  if (field.type === 'FILE' || field.type === 'CHECKBOX') {
    return !Array.isArray(value) || value.length === 0
  }
  return value === undefined || value === null || value === ''
}

// 监听表单数据变化，动态更新流程预览
watch(
  () => initiateForm.formData,
  () => {
    if (selectedWorkflow.value && currentStep.value === 2) {
      loadApprovalPath()
    }
  },
  { deep: true }
)

async function submitInitiate() {
  if (!initiateForm.title) {
    ElMessage.warning('请输入申请标题')
    return
  }
  if (workflowFileUploading.value > 0) {
    ElMessage.warning('附件上传中，请稍后提交')
    return
  }

  // 校验动态表单的必填项
  const formFields = workflowFormFields.value
  for (const field of formFields) {
    if (field.required) {
      const value = initiateForm.formData[field.key]
      if (isWorkflowFieldEmpty(field, value)) {
        ElMessage.warning(`请填写${field.label}`)
        return
      }
    }
  }

  const pathLoaded = await loadApprovalPath(true)
  if (!pathLoaded) {
    return
  }

  if (hasMissingRequiredParticipantSelections()) {
    validateParticipantSelections()
    return
  }

  if (!validateParticipantSelections()) {
    return
  }

  try {
    const submitData = {
      templateCode: selectedWorkflow.value.templateCode,
      title: initiateForm.title,
      priority: priorityValueToNum[initiateForm.priority] || 0,
      description: initiateForm.description,
      formData: buildSubmitFormData(),
      selectedApproverUserIds: participantSelections.approvers,
      selectedCcUserIds: participantSelections.cc
    }

    await startApproval(submitData)
    ElMessage.success('审批申请已提交')
    closeInitiateModal()
    // 刷新列表或跳转到"我发起"tab
    activeTab.value = 'initiated'
  } catch (error) {
    ElMessage.error(error.message || '提交失败')
  }
}

// 发起审批方法
const getTypeBg = (type) => {
  const typeMap = {
    'proposal': 'bg-blue-100 text-blue-600',
    'franchisee': 'bg-purple-100 text-purple-600',
    'product': 'bg-green-100 text-green-600',
    'refund': 'bg-amber-100 text-amber-600',
    'wealth': 'bg-cyan-100 text-cyan-700'
  }
  return typeMap[type] || 'bg-slate-100 text-slate-600'
}

// 优先级样式映射
const priorityTextMap = {
  'urgent': 'bg-red-100 text-red-700',
  'high': 'bg-amber-100 text-amber-700',
  'normal': 'bg-slate-100 text-slate-600'
}

const getPriorityClass = (priority) => {
  return priorityTextMap[priority] || 'bg-slate-100 text-slate-600'
}

const openRejectModal = (approval) => {
  selectedApproval.value = approval
  rejectForm.reason = ''
  showRejectModal.value = true
  showDetailModal.value = false
}

// 通过审批
const approveItem = async (approval) => {
  try {
    await approveTask({
      instanceId: approval.instanceId || approval.id,
      actionType: 'AGREE',
      remark: approvalForm.comment
    })
    ElMessage.success('审批通过')
    syncHandledApproval(approval.id)
    showDetailModal.value = false
    // 刷新列表
    loadApprovalsByTab()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// 驳回审批
const confirmReject = async () => {
  if (!rejectForm.reason.trim()) {
    ElMessage.warning('请输入驳回原因')
    return
  }

  try {
    await approveTask({
      instanceId: selectedApproval.value.instanceId || selectedApproval.value.id,
      actionType: 'REJECT',
      remark: rejectForm.reason
    })
    ElMessage.success('已驳回')
    syncHandledApproval(selectedApproval.value.id)
    showRejectModal.value = false
    showDetailModal.value = false
    // 刷新列表
    loadApprovalsByTab()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}
</script>

<style scoped>
/* 优化表单标签样式 */
:deep(.el-form-item__label) {
  font-weight: 500;
  color: #374151;
  font-size: 13px;
  padding-bottom: 4px;
  line-height: 1.4;
}

:deep(.el-form-item) {
  margin-bottom: 0;
}

:deep(.el-form-item__error) {
  padding-top: 2px;
  font-size: 12px;
}

/* 流程类型图标背景色 */
.type-proposal {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
}

.type-franchisee {
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  color: #9333ea;
}

.type-product {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #16a34a;
}

.type-refund {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
}

.type-default {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #64748b;
}

.approval-node-mode-tag {
  display: inline-flex;
  align-items: center;
  height: 20px;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 4px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
  vertical-align: middle;
}
</style>
